import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CheckCircle2, Loader2, MailWarning, Send } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "../context/AuthContext";
import LanguageSwitcher from "../components/LanguageSwitcher";
import Footer from "../components/Footer";

/**
 * Public page. Consumes `?token=` from the URL and calls the backend to
 * confirm the email. Success is the primary rendering path; failure has an
 * inline "Reinvia email" form because the confirmation link is by far the
 * most common way users land here.
 */
export default function VerifyEmail() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const { user, reload } = useAuth();

  const [state, setState] = useState(token ? "verifying" : "no_token"); // verifying | success | invalid | expired | no_token
  const [confirmedEmail, setConfirmedEmail] = useState("");
  const [resendEmail, setResendEmail] = useState("");
  const [resending, setResending] = useState(false);
  const [resendMsg, setResendMsg] = useState(null);
  const [resendErr, setResendErr] = useState(null);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.post("/auth/verify-email", { token });
        if (cancelled) return;
        setConfirmedEmail(data.email || "");
        setState("success");
        if (user) {
          try { await reload(); } catch { /* ignore */ }
        }
      } catch (e) {
        if (cancelled) return;
        const code = e?.response?.data?.detail?.code;
        setState(code === "expired_token" ? "expired" : "invalid");
      }
    })();
    return () => { cancelled = true; };
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const submitResend = async (e) => {
    e.preventDefault();
    if (!resendEmail.trim()) return;
    setResending(true);
    setResendMsg(null);
    setResendErr(null);
    try {
      await api.post("/auth/resend-verification", { email: resendEmail.trim() });
      setResendMsg(t("verify_email.resend_sent"));
    } catch (err) {
      setResendErr(formatApiError(err));
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="verify-email-page">
      <div className="flex justify-between items-center p-5 md:p-6">
        <Link to="/" className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-[#0A0A0B] flex items-center justify-center">
            <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none">
              <path d="M4 20 L12 4 L20 20 L17 20 L15.4 16 L8.6 16 L7 20 Z" fill="white" />
              <path d="M9.7 13 L12 8 L14.3 13 Z" fill="#2563EB" />
            </svg>
          </div>
          <div className="font-heading font-bold text-lg tracking-tight text-slate-900">AUTEXIO</div>
        </Link>
        <LanguageSwitcher />
      </div>

      <div className="flex-1 flex items-center justify-center px-6 pb-16">
        <div className="w-full max-w-md">
          {state === "verifying" && (
            <div className="text-center" data-testid="verify-state-verifying">
              <Loader2 className="w-10 h-10 mx-auto text-blue-600 animate-spin" />
              <h2 className="mt-6 font-heading font-bold text-2xl tracking-tight text-slate-900">
                {t("verify_email.verifying_title")}
              </h2>
              <p className="mt-2 text-sm text-slate-500">{t("verify_email.verifying_hint")}</p>
            </div>
          )}

          {state === "success" && (
            <div className="text-center" data-testid="verify-state-success">
              <div className="w-14 h-14 mx-auto rounded-full bg-emerald-50 border border-emerald-100 flex items-center justify-center">
                <CheckCircle2 className="w-7 h-7 text-emerald-600" />
              </div>
              <h2 className="mt-6 font-heading font-bold text-3xl tracking-tighter text-slate-900">
                {t("verify_email.success_title")}
              </h2>
              <p className="mt-3 text-sm text-slate-600 leading-relaxed">
                {t("verify_email.success_body")}
              </p>
              {confirmedEmail && (
                <p className="mt-1 text-xs text-slate-400">{confirmedEmail}</p>
              )}
              <div className="mt-8 flex flex-col gap-3">
                <Button
                  data-testid="verify-goto-login"
                  onClick={() => navigate(user ? "/home" : "/login")}
                  className="w-full h-11 rounded-full bg-blue-600 hover:bg-blue-700 text-white font-semibold"
                >
                  {user ? t("verify_email.goto_home") : t("verify_email.goto_login")}
                </Button>
              </div>
            </div>
          )}

          {(state === "invalid" || state === "expired" || state === "no_token") && (
            <div data-testid={`verify-state-${state}`}>
              <div className="w-14 h-14 mx-auto rounded-full bg-red-50 border border-red-100 flex items-center justify-center">
                <MailWarning className="w-7 h-7 text-red-600" />
              </div>
              <h2 className="mt-6 font-heading font-bold text-3xl tracking-tighter text-slate-900 text-center">
                {state === "expired"
                  ? t("verify_email.expired_title")
                  : state === "no_token"
                  ? t("verify_email.no_token_title")
                  : t("verify_email.invalid_title")}
              </h2>
              <p className="mt-3 text-sm text-slate-600 leading-relaxed text-center">
                {state === "expired"
                  ? t("verify_email.expired_body")
                  : t("verify_email.invalid_body")}
              </p>

              <form onSubmit={submitResend} className="mt-8 space-y-3" data-testid="verify-resend-form">
                <div>
                  <Label htmlFor="resend-email" className="text-xs font-medium text-slate-600">
                    {t("verify_email.resend_label")}
                  </Label>
                  <Input
                    id="resend-email"
                    type="email"
                    required
                    autoComplete="email"
                    data-testid="verify-resend-email"
                    value={resendEmail}
                    onChange={(e) => setResendEmail(e.target.value)}
                    className="mt-1 h-11 rounded-xl border-slate-200"
                  />
                </div>
                {resendErr && (
                  <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="verify-resend-error">
                    {resendErr}
                  </div>
                )}
                {resendMsg && (
                  <div className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2" data-testid="verify-resend-success">
                    {resendMsg}
                  </div>
                )}
                <Button
                  type="submit"
                  disabled={resending}
                  data-testid="verify-resend-submit"
                  className="w-full h-11 rounded-full bg-blue-600 hover:bg-blue-700 text-white font-semibold"
                >
                  {resending ? <Loader2 className="w-4 h-4 animate-spin" /> : (<><Send className="w-4 h-4 mr-2" /> {t("verify_email.resend_button")}</>)}
                </Button>
              </form>

              <p className="mt-6 text-center text-[12px] text-slate-500">
                <Link to="/login" className="hover:text-slate-900 underline-offset-4 hover:underline">
                  {t("verify_email.back_to_login")}
                </Link>
              </p>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
